import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateAppModal from "../components/CreateAppModal";
import DataRowTable from "../components/DataRowTable";
import RowActionBar from "../components/RowActionBar";
import { Badge, PageWait, recTone } from "../lib/ui";
import { appOpenRoute, canCreateApp } from "../lib/master";
import { withApp } from "../lib/story";

const APP_COLUMNS = [
  { key: "id", label: "ID", width: "10%" },
  { key: "name", label: "Name", width: "22%" },
  { key: "domain", label: "Domain", width: "14%" },
  { key: "criticality", label: "Criticality", width: "10%" },
  {
    key: "recommendation",
    label: "7R / Stage",
    width: "14%",
    render: (a) => <Badge tone={recTone(a.recommendation || a.stage)}>{a.recommendation || a.pending_gate || a.stage}</Badge>,
  },
  {
    key: "gates",
    label: "Gates",
    width: "12%",
    render: (a) => (a.gate_progress ? `${a.gate_progress.passed}/${a.gate_progress.total}` : "—"),
  },
  {
    key: "mdr",
    label: "MDR",
    width: "12%",
    render: (a) => `${a.mdr_version || "—"} · ${a.mdr_state || "—"}`,
  },
];

export default function Applications({ portfolio, user, onCreateApp }) {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  if (!portfolio) return <PageWait label="Loading applications…" inline />;

  const { kpis, applications } = portfolio;
  const guide = user?.guide || portfolio.master?.guide || {};
  const mayCreate = canCreateApp(guide);
  const distEntries = Object.entries(kpis?.distribution || {}).sort((a, b) => b[1] - a[1]);
  const maxCount = Math.max(1, ...distEntries.map(([, v]) => v));

  const openApp = (a) => navigate(withApp(appOpenRoute(guide), a.id));

  return (
    <div className="dashboard inner-page" data-testid="applications-page">
      <div className="section-title">
        <div>
          <p className="kicker">APPLICATIONS · GOVERNED DECISION UNITS</p>
          <h2>{(applications || []).length} modernization projects</h2>
          <p className="muted">Row table with always-visible View — open any application workspace. Tenant NRG-01 only.</p>
        </div>
        <div className="intro-actions">
          {mayCreate && (
            <button data-testid="portfolio-create-app" className="primary-button" type="button" onClick={() => setCreateOpen(true)}>
              Create application
            </button>
          )}
        </div>
      </div>

      {distEntries.length > 0 && (
        <section className="feature-panel analytics-panel">
          <div className="panel-header">
            <div>
              <p className="kicker">7R STRATEGY MIX</p>
              <h2>Recommendation distribution</h2>
            </div>
            <Badge tone="cyan">TENANT SCOPED</Badge>
          </div>
          <div className="dist-list">
            {distEntries.map(([name, count]) => (
              <div key={name} data-testid={`dist-${name.toLowerCase().replaceAll(" ", "-").replaceAll("/", "-")}`}>
                <span className="dist-label"><span className={`dist-swatch ${recTone(name)}`} /> {name}</span>
                <span className="dist-bar-wrap"><span className={`dist-bar ${recTone(name)}`} style={{ width: `${(count / maxCount) * 100}%` }} /></span>
                <b>{count}</b>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="feature-panel" data-testid="portfolio-app-list">
        <DataRowTable
          testId="portfolio-app-table"
          columns={APP_COLUMNS}
          rows={applications || []}
          rowKey="id"
          pageSize={10}
          emptyMessage="No applications in this tenant."
          hint="Click View to open the application decision workspace."
          renderActions={(row, stableId) => (
            <RowActionBar
              testId={`app-actions-${stableId}`}
              rowId={stableId}
              view
              onView={() => openApp(row)}
            />
          )}
        />
      </section>

      {createOpen && (
        <CreateAppModal
          onClose={() => setCreateOpen(false)}
          onCreate={async (body) => {
            const created = await onCreateApp(body);
            setCreateOpen(false);
            if (created?.id) navigate(withApp("/overview", created.id));
          }}
        />
      )}
    </div>
  );
}
